import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDateTimeFull } from '@/utils/format';

export type TransactionItem = {
  id: string;
  type: string;
  label: string | null;
  counterparty: string;
  reference: string | null;
  amount: number;
  fee: number;
  currency: string | null;
  date: string;
  status: string;
};

type TransactionListProps = {
  items: TransactionItem[];
  currency: string;
};

const typeConfig: Record<string, { icon: string; tone: string; labelKey: string }> = {
  transfer: {
    icon: 'ri-arrow-down-line',
    tone: 'bg-primary-100 text-primary-700',
    labelKey: 'dashboard.op.transfer',
  },
  withdrawal: {
    icon: 'ri-arrow-up-line',
    tone: 'bg-secondary-100 text-secondary-800',
    labelKey: 'dashboard.op.withdrawal',
  },
  fee: {
    icon: 'ri-percent-line',
    tone: 'bg-background-200 text-foreground-600',
    labelKey: 'dashboard.op.fee',
  },
};

const statusConfig: Record<string, { labelKey: string; tone: string }> = {
  done: { labelKey: 'dashboard.op.done', tone: 'bg-primary-50 text-primary-800' },
  pending: { labelKey: 'dashboard.op.pending', tone: 'bg-accent-100 text-accent-900' },
  refused: { labelKey: 'dashboard.op.refused', tone: 'bg-secondary-100 text-secondary-900' },
};

export default function TransactionList({ items, currency }: TransactionListProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-heading text-base font-semibold text-foreground-950">
          {t('dashboard.recent')}
        </h2>
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-history-line"></i>
        </span>
      </div>

      {items.length === 0 ? (
        <p className="mt-6 text-sm text-foreground-500">{t('dashboard.recentEmpty')}</p>
      ) : (
        <ul className="mt-4 divide-y divide-background-200">
          {items.map((item) => {
            const type = typeConfig[item.type] ?? typeConfig.fee;
            const status = statusConfig[item.status] ?? statusConfig.pending;
            const positive = item.amount >= 0;
            return (
              <li key={item.id} className="py-4">
                <div className="flex items-start gap-4">
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${type.tone}`}
                  >
                    <i className={`${type.icon} text-lg`}></i>
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-foreground-950">
                        {t(type.labelKey)}
                      </p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${status.tone}`}
                      >
                        {t(status.labelKey)}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-foreground-600">
                      {item.counterparty}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-foreground-500">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-hashtag"></i>
                        <span className="font-mono">{item.reference ?? t('dashboard.txNoRef')}</span>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {formatDateTimeFull(item.date)}
                      </span>
                      {item.fee > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-percent-line"></i>
                          {t('common.serviceFee')}: {formatCurrency(item.fee, item.currency || currency)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-foreground-400">
                      {positive ? t('dashboard.txReceived') : t('dashboard.txAmount')}
                    </span>
                    <span
                      className={`text-sm font-semibold ${
                        positive ? 'text-primary-700' : 'text-foreground-800'
                      }`}
                    >
                      {positive ? '+' : '-'} {formatCurrency(Math.abs(item.amount), item.currency || currency)}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}